<template>
  <section class="stack" aria-live="polite">
    <Card>
      <CardHeader class="stack">
        <div class="ui-row">
          <Badge>Account Access</Badge>
          <Badge variant="outline">Protected</Badge>
        </div>
        <h2 class="page-title">{{ title }}</h2>
      </CardHeader>
      <CardContent class="stack">
        <p class="lede">
          {{ body }}
        </p>
        <div v-if="session.state !== 'loading'" class="auth-gate-actions">
          <Button variant="outline" color="neutral" @click="openModal('dev')">
            Use dev token
          </Button>
          <Button variant="outline" color="neutral" @click="openModal('token')">
            Use access token
          </Button>
        </div>
      </CardContent>
    </Card>

    <!-- Dev Token Modal -->
    <div
      v-if="activeModal === 'dev'"
      class="modal-overlay"
      role="presentation"
      @click.self="closeModal"
    >
      <Card class="modal-card">
        <CardHeader class="stack">
          <div class="ui-row">
            <Badge>Developer Access</Badge>
            <Badge variant="outline">Local only</Badge>
          </div>
          <h2 class="page-title">Use a dev token on this device.</h2>
          <p class="lede">
            The token is exchanged for a session cookie and stored only in this browser.
          </p>
        </CardHeader>
        <CardContent class="stack">
          <form class="field-stack" @submit.prevent="submitDevToken">
            <Label for="dev-token">Dev token</Label>
            <Input
              id="dev-token"
              v-model="devTokenInput"
              type="password"
              placeholder="Paste dev token"
              :disabled="isSubmitting"
              required
            />
            <p v-if="devTokenError" class="search-hint auth-gate-error">{{ devTokenError }}</p>
            <div class="auth-gate-actions">
              <Button variant="outline" color="neutral" :disabled="isSubmitting" @click="closeModal">
                Cancel
              </Button>
              <Button type="submit" :loading="isSubmitting" :disabled="!trimmedDevToken">
                Continue
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>

    <!-- Token Modal -->
    <div
      v-if="activeModal === 'token'"
      class="modal-overlay"
      role="presentation"
      @click.self="closeModal"
    >
      <Card class="modal-card">
        <CardHeader class="stack">
          <div class="ui-row">
            <Badge>Access Token</Badge>
            <Badge variant="outline">API Key</Badge>
          </div>
          <h2 class="page-title">Sign in with an access token.</h2>
          <p class="lede">
            Paste your issued access token to create a session. The token is exchanged for a session cookie.
          </p>
        </CardHeader>
        <CardContent class="stack">
          <form class="field-stack" @submit.prevent="submitToken">
            <Label for="access-token">Access token</Label>
            <Input
              id="access-token"
              v-model="tokenInput"
              type="password"
              placeholder="seeft_tok_..."
              :disabled="isSubmitting"
              required
            />
            <p v-if="tokenError" class="search-hint auth-gate-error">{{ tokenError }}</p>
            <div class="auth-gate-actions">
              <Button variant="outline" color="neutral" :disabled="isSubmitting" @click="closeModal">
                Cancel
              </Button>
              <Button type="submit" :loading="isSubmitting" :disabled="!trimmedToken">
                Sign In
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { Badge, Button, Card, CardContent, CardHeader, Input, Label } from "../ui";
import { useSessionStore } from "../stores/session";

const session = useSessionStore();

const activeModal = ref<"dev" | "token" | null>(null);
const isSubmitting = ref(false);

// Dev token state
const devTokenInput = ref("");
const devTokenError = ref<string | null>(null);

// Token state
const tokenInput = ref("");
const tokenError = ref<string | null>(null);

const title = computed(() => {
  if (session.state === "loading") {
    return "Checking session";
  }

  if (session.state === "auth-error") {
    return "Could not verify access";
  }

  return "Sign in required";
});

const body = computed(() => {
  if (session.state === "loading") {
    return "Verifying your Seeft session.";
  }

  if (session.state === "auth-error") {
    return session.authError ?? "This route needs a valid Seeft session.";
  }

  return "This route needs an authenticated Seeft session. Sign in with Google/Microsoft, or use a dev token/access token.";
});

const trimmedDevToken = computed(() => devTokenInput.value.trim());
const trimmedToken = computed(() => tokenInput.value.trim());

function openModal(type: "dev" | "token") {
  activeModal.value = type;
  isSubmitting.value = false;
  devTokenError.value = null;
  tokenError.value = null;
}

function closeModal() {
  activeModal.value = null;
  isSubmitting.value = false;
  devTokenError.value = null;
  tokenError.value = null;
}

async function submitDevToken() {
  if (!trimmedDevToken.value || isSubmitting.value) {
    return;
  }

  isSubmitting.value = true;
  devTokenError.value = null;

  const success = await session.signInWithDevToken(trimmedDevToken.value);

  if (success) {
    devTokenInput.value = "";
    closeModal();
    return;
  }

  devTokenError.value = session.authError ?? "Dev token was not accepted.";
  isSubmitting.value = false;
}

async function submitToken() {
  if (!trimmedToken.value || isSubmitting.value) {
    return;
  }

  isSubmitting.value = true;
  tokenError.value = null;

  const success = await session.signInWithToken(trimmedToken.value);

  if (success) {
    tokenInput.value = "";
    closeModal();
    return;
  }

  tokenError.value = session.authError ?? "Token was not accepted.";
  isSubmitting.value = false;
}
</script>
